import { useTranslation } from "react-i18next";
import { JobPartCategory } from "../../models/Job";

interface VehiclePartSummaryProps {
  jobPartCategories: JobPartCategory[];
  showHeader?: boolean;
}

export default function VehiclePartSummary({ jobPartCategories, showHeader = true }: VehiclePartSummaryProps) {
  const { t } = useTranslation("jobs");
  const parts = jobPartCategories
    .map((x) => x.partData)
    .filter((x) => x !== null);

  const totalParts = parts.length;
  // Priority order: removed -> sold -> damaged -> missing. Missing gets its own band because it
  // is a third way a part leaves the dismantler's list, and without it those parts sat in
  // "other" and read as still outstanding.
  const removedCount = parts.filter((part) => !!part?.removedOn).length;
  const soldCount = parts.filter((part) => !part?.removedOn && !!part?.soldDate).length;
  const damagedCount = parts.filter((part) => !part?.removedOn && !part?.soldDate && !!part?.isDamaged).length;
  const missingCount = parts.filter(
    (part) => !part?.removedOn && !part?.soldDate && !part?.isDamaged && !!part?.isMissing
  ).length;
  const otherCount = Math.max(totalParts - removedCount - soldCount - damagedCount - missingCount, 0);
  const partsToRemove = Math.max(totalParts - removedCount - missingCount, 0);
  const removedPercent = totalParts > 0 ? (removedCount / totalParts) * 100 : 0;
  const soldPercent = totalParts > 0 ? (soldCount / totalParts) * 100 : 0;
  const damagedPercent = totalParts > 0 ? (damagedCount / totalParts) * 100 : 0;
  const missingPercent = totalParts > 0 ? (missingCount / totalParts) * 100 : 0;

  return (
    <div className="rounded-md border border-gray-200 bg-white px-4 py-3">
      {showHeader && (
        <div className="mb-2 flex items-start justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{t("Vehicle Part Summary")}</p>
            <p className="text-2xl font-semibold text-gray-800">{t("{{count}} Total", { count: totalParts })}</p>
          </div>
        </div>
      )}

      <div className="mt-3">
        <div className="flex h-2 w-full overflow-hidden rounded-full bg-gray-200">
          <div
            className="h-2 bg-primary-500 transition-all duration-300"
            style={{ width: `${removedPercent}%` }}
          />
          <div
            className="h-2 bg-blue-500 transition-all duration-300"
            style={{ width: `${soldPercent}%` }}
          />
          <div
            className="h-2 bg-rose-500 transition-all duration-300"
            style={{ width: `${damagedPercent}%` }}
          />
          <div
            className="h-2 bg-amber-500 transition-all duration-300"
            style={{ width: `${missingPercent}%` }}
          />
        </div>
        <div className="mt-2 flex items-center justify-between text-xs">
          <span className="font-medium text-gray-600">{t("{{count}} removed", { count: removedCount })}</span>
          <span className="font-medium text-blue-600">{t("{{count}} sold", { count: soldCount })}</span>
          <span className="font-medium text-rose-600">{t("{{count}} damaged", { count: damagedCount })}</span>
          <span className="font-medium text-amber-600">{t("{{count}} missing", { count: missingCount })}</span>
          {otherCount > 0 && (
            <span className="font-medium text-gray-400">{t("{{count}} other", { count: otherCount })}</span>
          )}
        </div>
      </div>
    </div>
  );
}
